import React, { useMemo } from "react";
import { Box, Typography, TextField, styled, Button } from "@mui/material";
import { PinIcon, LocationIcon } from "../../assets/icons";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { checkEmptyObj } from "../../utils";
import {
  TypewriterText,
  StaggeredText,
  BouncingText,
  ShimmerText,
  GlowPulseText,
  SplitText,
  useTextAnimation,
} from "./TextAnimations";

// Hero Section Styles (keeping original styles)
const HeroSection = styled(Box)(({ theme }) => ({
  position: "relative",
  width: "100%",
  height: "779px",
  backgroundImage: "url(/images/section1.svg)",
  backgroundSize: "cover",
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",
  overflow: "hidden",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  "&::before": {
    content: '""',
    position: "absolute",
    top: "-109px",
    left: "-70px",
    width: "2125px",
    height: "819px",
    zIndex: 0,
  },
  [theme.breakpoints.down("lg")]: {
    height: "700px",
  },
  [theme.breakpoints.down("md")]: {
    height: "600px",
    paddingTop: "0px",
  },
}));

const HeroContent = styled(Box)(({ theme }) => ({
  position: "relative",
  zIndex: 2,
  display: "flex",
  width: "1440px",
  maxWidth: "100%",
  padding: "0 130px",
  alignItems: "center",
  gap: "80px",
  [theme.breakpoints.down("lg")]: {
    flexDirection: "column",
    padding: "0 24px",
    gap: "32px",
  },
  [theme.breakpoints.down("md")]: {
    padding: "24px 20px 0",
    gap: "24px",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "16px 16px 0",
    gap: "40px",
  },
}));

const HeroTextSection = styled(Box)(({ theme }) => ({
  width: "548px",
  display: "flex",
  flexDirection: "column",
  gap: "24px",
  [theme.breakpoints.down("lg")]: {
    width: "100%",
    textAlign: "center",
  },
  [theme.breakpoints.down("md")]: {
    gap: "20px",
  },
  [theme.breakpoints.down("sm")]: {
    gap: "16px",
  },
}));

const HeroTitleContainer = styled(Box)(({ theme }) => ({
  fontFamily: "Inter",
  fontWeight: 700,
  fontSize: "32px",
  lineHeight: "1.3",
  color: "#14582D",
  [theme.breakpoints.down("md")]: {
    fontSize: "24px",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "24px",
  },
}));

const HeroSubtitleContainer = styled(Box)(({ theme }) => ({
  fontFamily: "Inter",
  fontWeight: 400,
  fontSize: "18px",
  lineHeight: "1.5",
  color: "#14582D",
  [theme.breakpoints.down("md")]: {
    fontSize: "16px",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "14px",
  },
}));

const AddressInputContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  width: "387px",
  [theme.breakpoints.down("sm")]: {
    width: "100%",
  },
}));

const AddressInputField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#FFFFFF",
    borderRadius: "12px",
    height: "56px",
    display: "flex",
    alignItems: "center",
    padding: "16px",
    "& fieldset": {
      borderColor: "#CFD1D5",
    },
    "&:hover fieldset": {
      borderColor: "#29BC62",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#29BC62",
    },
    "& .MuiInputBase-input::placeholder": {
      color: theme.palette.text.fontDarkest,
      opacity: 1,
    },
    "& input::placeholder": {
      color: theme.palette.text.fontDarkest,
      opacity: 1,
    },
  },
}));

const PhoneMockup = styled(Box)<{ language: string }>(({ theme, language }) => ({
  width: "703px",
  height: "689px",
  backgroundImage:
    language === "ar" ? "url(/images/hero-phone-mockup-ar.png)" : "url(/images/hero-phone-mockup-en.png)",
  backgroundSize: "cover",
  backgroundRepeat: "no-repeat",
  backgroundPosition: "center",
  animation: "float 6s ease-in-out infinite",
  "@keyframes float": {
    "0%": { transform: "translateY(0px)" },
    "50%": { transform: "translateY(-20px)" },
    "100%": { transform: "translateY(0px)" },
  },
  [theme.breakpoints.down("lg")]: {
    width: "400px",
    height: "392px",
  },
  [theme.breakpoints.down("sm")]: {
    width: "300px",
    height: "294px",
  },
}));

const HeroButton = styled(Button)(({ theme }) => ({
  background: "#F5933D",
  color: "#FFFFFF",
  borderRadius: "12px",
  padding: "16px 40px",
  fontSize: "16px",
  fontWeight: 600,
  fontFamily: "Inter",
  textTransform: "none",
  maxWidth: "200px",
  "&:hover": {
    background: "#E8843A",
    transform: "translateY(-2px) scale(1.05)",
    boxShadow: "0 8px 25px rgba(245, 147, 61, 0.4)",
  },
  "&:active": {
    transform: "translateY(0) scale(0.98)",
  },
  transition: "all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
}));

interface EnhancedHeroSectionProps {
  animationVariant?: "typewriter" | "staggered" | "bouncing" | "shimmer" | "glow" | "split";
}

const EnhancedHeroSectionComponent: React.FC<EnhancedHeroSectionProps> = ({ animationVariant = "typewriter" }) => {
  const { t, i18n } = useTranslation();
  const { elementRef, isVisible } = useTextAnimation(0.2);
  const user = useSelector((state: RootState) => state.user);

  const isLoggedIn = useMemo(() => !checkEmptyObj(user), [user]);
  const buttonLink = isLoggedIn ? "/recommended-plans" : "/find-best-plan";

  const renderTitle = () => {
    const titleText = t("common.enhancedHeroSection.title");

    switch (animationVariant) {
      case "typewriter":
        return (
          <TypewriterText
            text={titleText}
            speed={80}
            delay={0.5}
            trigger={isVisible}
            component="div"
            sx={{
              fontFamily: "Inter",
              fontWeight: 700,
              fontSize: "inherit",
              lineHeight: "inherit",
              color: "inherit",
            }}
          />
        );

      case "staggered":
        return (
          <StaggeredText
            text={titleText}
            staggerBy="words"
            staggerDelay={0.15}
            delay={0.3}
            trigger={isVisible}
            component="div"
            sx={{
              fontFamily: "Inter",
              fontWeight: 700,
              fontSize: "inherit",
              lineHeight: "inherit",
              color: "inherit",
            }}
          />
        );

      case "bouncing":
        return (
          <BouncingText
            delay={0.5}
            trigger={isVisible}
            component="div"
            sx={{
              fontFamily: "Inter",
              fontWeight: 700,
              fontSize: "inherit",
              lineHeight: "inherit",
              color: "inherit",
            }}
          >
            {titleText}
          </BouncingText>
        );

      case "shimmer":
        return (
          <ShimmerText
            delay={0.3}
            trigger={isVisible}
            component="div"
            sx={{
              fontFamily: "Inter",
              fontWeight: 700,
              fontSize: "inherit",
              lineHeight: "inherit",
            }}
          >
            {titleText}
          </ShimmerText>
        );

      case "glow":
        return (
          <GlowPulseText
            delay={0.5}
            trigger={isVisible}
            component="div"
            sx={{
              fontFamily: "Inter",
              fontWeight: 700,
              fontSize: "inherit",
              lineHeight: "inherit",
            }}
          >
            {titleText}
          </GlowPulseText>
        );

      case "split":
        return (
          <SplitText
            text={titleText}
            splitBy="words"
            animationType="slideUp"
            staggerDelay={0.1}
            delay={0.3}
            trigger={isVisible}
            component="div"
            sx={{
              fontFamily: "Inter",
              fontWeight: 700,
              fontSize: "inherit",
              lineHeight: "inherit",
              color: "inherit",
            }}
          />
        );

      default:
        return titleText;
    }
  };

  const renderSubtitle = () => {
    const subtitleText = t("common.enhancedHeroSection.subtitle");

    return (
      <StaggeredText
        text={subtitleText}
        staggerBy="words"
        staggerDelay={0.08}
        delay={1.5}
        trigger={isVisible}
        component="div"
        sx={{
          fontFamily: "Inter",
          fontWeight: 400,
          fontSize: "inherit",
          lineHeight: "inherit",
          color: "inherit",
        }}
      />
    );
  };

  return (
    <HeroSection ref={elementRef}>
      <HeroContent>
        <HeroTextSection>
          <HeroTitleContainer>{renderTitle()}</HeroTitleContainer>

          <HeroSubtitleContainer>{renderSubtitle()}</HeroSubtitleContainer>

          <Box
            sx={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? "translateY(0)" : "translateY(20px)",
              transition: "all 0.8s ease-out 1.0s",
            }}
          >
            <Link style={{ textDecoration: "none", color: "#FFFFFF" }} to={buttonLink}>
              <HeroButton>{t("common.enhancedHeroSection.exploreButton")}</HeroButton>
            </Link>
          </Box>
        </HeroTextSection>
        <PhoneMockup language={i18n.language} />
      </HeroContent>
    </HeroSection>
  );
};

export default EnhancedHeroSectionComponent;
